import {
  isConsentChallenge,
  isTwoFactorChallenge,
} from "@nexys/user-management-client";
import { AlertCircle, Fingerprint, Mail, MailCheck, ShieldCheck, Wand2 } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { errorMessage } from "../../lib/error-message.js";
import {
  getPasskeyAssertion,
  webauthnErrorMessage,
  webauthnSupported,
} from "../../lib/webauthn.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import { Separator } from "../ui/separator.js";

const providerLabel: Record<string, string> = {
  google: "Google",
  github: "GitHub",
  microsoft: "Microsoft",
  discord: "Discord",
  facebook: "Facebook",
};

const ProviderIcon = ({ provider }: { provider: string }) => {
  switch (provider) {
    case "google":
      return (
        <svg className="size-4" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      );
    case "github":
      return (
        <svg className="size-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
        </svg>
      );
    default:
      return null;
  }
};

export interface SignInFormProps {
  onSuccess?: (user: { id: string; email: string }) => void;
  forgotPasswordSlot?: ReactNode;
  signUpSlot?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  /**
   * Social providers to offer as "Continue with …" buttons. Social
   * sign-in only works for providers a product has an enabled
   * connection for, so pass the `/api/providers?projectId=…` list (and
   * `projectId` below) — there is no default: without this the form is
   * email/passkey/magic-link only.
   */
  socialProviders?: string[];
  /** Product whose social connections back the buttons above. */
  projectId?: string;
}

export const SignInForm = ({
  onSuccess,
  forgotPasswordSlot,
  signUpSlot,
  title = "Welcome back",
  description = "Sign in to your account to continue.",
  socialProviders,
  projectId,
}: SignInFormProps) => {
  const client = useUmClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const providers = socialProviders ?? [];
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  // Non-null once a password sign-in came back `twoFactorRequired` —
  // the form switches to the one-time-code step.
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  // Magic-link flow: "form" is the email-only step, "sent" the
  // check-your-inbox confirmation.
  const [magicStep, setMagicStep] = useState<"off" | "form" | "sent">("off");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await client.signIn({ email: email.trim(), password });
    setLoading(false);
    if (res.ok) {
      if (isTwoFactorChallenge(res.data)) {
        setChallengeToken(res.data.twoFactorToken);
      } else if (isConsentChallenge(res.data)) {
        // A blocking agreement is outstanding, so no session was issued.
        // The console isn't the place to accept product terms — point the
        // user at the app that asked for them.
        setError(
          `You need to accept "${res.data.documents[0]?.title ?? "an agreement"}" before signing in.`
        );
      } else {
        onSuccess?.({ id: res.data.user.id, email: res.data.user.email });
      }
    } else {
      setError(errorMessage(res.error, "Sign-in failed. Please try again."));
    }
  };

  const submitOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (!challengeToken) return;
    setLoading(true);
    setError(null);
    const verify = useBackupCode ? client.verifyBackupCode : client.verifyTotp;
    const res = await verify({
      token: challengeToken,
      code: otp.trim(),
    });
    setLoading(false);
    if (res.ok) {
      if (isConsentChallenge(res.data)) {
        // Same gate as password sign-in — no session was issued.
        setError(
          `You need to accept "${res.data.documents[0]?.title ?? "an agreement"}" before signing in.`
        );
      } else {
        onSuccess?.({ id: res.data.user.id, email: res.data.user.email });
      }
    } else {
      setError(errorMessage(res.error, "That code didn't match — try again."));
    }
  };

  const handleSocial = async (provider: string) => {
    setSocialLoading(provider);
    setError(null);
    const callbackURL = window.location.origin + "/";
    const res = await client.socialSignIn({ provider, callbackURL, projectId });
    if (res.ok) {
      window.location.href = res.data.url;
    } else {
      setSocialLoading(null);
      setError(errorMessage(res.error, `Sign-in with ${providerLabel[provider] ?? provider} failed.`));
    }
  };

  const handlePasskey = async () => {
    setPasskeyLoading(true);
    setError(null);
    try {
      const start = await client.passkeySignInStart({
        email: email.trim() || undefined,
      });
      if (!start.ok) {
        setError(errorMessage(start.error, "Couldn't start passkey sign-in."));
        return;
      }
      const assertion = await getPasskeyAssertion(start.data.options);
      const finish = await client.passkeySignInFinish({
        challenge: start.data.challenge,
        response: assertion,
      });
      if (finish.ok) {
        if (isConsentChallenge(finish.data)) {
          // Same gate as password sign-in — no session was issued.
          setError(
            `You need to accept "${finish.data.documents[0]?.title ?? "an agreement"}" before signing in.`
          );
        } else {
          onSuccess?.({ id: finish.data.user.id, email: finish.data.user.email });
        }
      } else {
        setError(errorMessage(finish.error, "Passkey sign-in failed."));
      }
    } catch (err) {
      // The browser throws when the user dismisses the platform dialog.
      setError(webauthnErrorMessage(err, "Passkey sign-in failed."));
    } finally {
      setPasskeyLoading(false);
    }
  };

  const submitMagicLink = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await client.requestMagicLink({ email: email.trim() });
    setLoading(false);
    if (res.ok) {
      setMagicStep("sent");
    } else {
      setError(errorMessage(res.error, "Couldn't send the sign-in link."));
    }
  };

  // ── Magic-link steps ──────────────────────────────────────────────
  if (magicStep === "sent") {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-2xl">
            <MailCheck className="size-5 text-muted-foreground" />
            Check your inbox
          </CardTitle>
          <CardDescription>
            If an account exists for <span className="font-medium">{email.trim()}</span>,
            a sign-in link is on its way. It expires in 15 minutes and works once.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => {
              setMagicStep("off");
              setError(null);
            }}
          >
            Back to sign-in
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (magicStep === "form") {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Wand2 className="size-5 text-muted-foreground" />
            Email me a sign-in link
          </CardTitle>
          <CardDescription>
            No password needed — we&apos;ll send a one-time link to your inbox.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitMagicLink} className="grid gap-4" noValidate>
            <Field label="Email" htmlFor="magic-email" required>
              <div className="relative">
                <Mail
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  id="magic-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  placeholder="you@example.com"
                />
              </div>
            </Field>
            {error ? (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <Button
              type="submit"
              className="w-full"
              loading={loading}
              disabled={!email.trim()}
            >
              Send sign-in link
            </Button>
            <button
              type="button"
              className="text-center text-sm text-muted-foreground underline-offset-4 hover:underline"
              onClick={() => {
                setMagicStep("off");
                setError(null);
              }}
            >
              Back to sign-in
            </button>
          </form>
        </CardContent>
      </Card>
    );
  }

  // ── 2FA challenge step ────────────────────────────────────────────
  if (challengeToken) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-2xl">
            <ShieldCheck className="size-5 text-muted-foreground" />
            Two-factor check
          </CardTitle>
          <CardDescription>
            {useBackupCode
              ? "Enter one of your single-use backup codes."
              : "Enter the 6-digit code from your authenticator app."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitOtp} className="grid gap-4" noValidate>
            <Field
              label={useBackupCode ? "Backup code" : "Verification code"}
              htmlFor="otp"
              required
            >
              <Input
                id="otp"
                inputMode={useBackupCode ? "text" : "numeric"}
                autoComplete="one-time-code"
                autoFocus
                required
                placeholder={useBackupCode ? "XXXX-XXXX" : "123456"}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="font-mono"
              />
            </Field>
            {error ? (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <Button type="submit" className="w-full" loading={loading}>
              Verify
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                className="text-muted-foreground underline-offset-4 hover:underline"
                onClick={() => {
                  setUseBackupCode((v) => !v);
                  setOtp("");
                  setError(null);
                }}
              >
                {useBackupCode ? "Use authenticator code" : "Use a backup code"}
              </button>
              <button
                type="button"
                className="text-muted-foreground underline-offset-4 hover:underline"
                onClick={() => {
                  setChallengeToken(null);
                  setOtp("");
                  setError(null);
                }}
              >
                Back to sign-in
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1.5">
        <CardTitle className="text-2xl">{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          {providers.length > 0 && (
            <>
              <div className="grid gap-2">
                {providers.map((p) => (
                  <Button
                    key={p}
                    type="button"
                    variant="outline"
                    className="w-full"
                    loading={socialLoading === p}
                    disabled={!!socialLoading || loading}
                    onClick={() => handleSocial(p)}
                  >
                    <ProviderIcon provider={p} />
                    Continue with {providerLabel[p] ?? p}
                  </Button>
                ))}
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>
            </>
          )}
          <form onSubmit={submit} className="grid gap-4" noValidate>
            <Field label="Email" htmlFor="email" required>
              <div className="relative">
                <Mail
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  placeholder="you@example.com"
                />
              </div>
            </Field>
            <Field
              label={
                <span className="flex w-full items-center justify-between">
                  <span>Password</span>
                  {forgotPasswordSlot}
                </span>
              }
              htmlFor="password"
              required
            >
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {error ? (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <Button type="submit" className="w-full" loading={loading}>
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            {webauthnSupported() ? (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                loading={passkeyLoading}
                disabled={loading || !!socialLoading}
                onClick={handlePasskey}
              >
                <Fingerprint />
                Sign in with a passkey
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={loading || !!socialLoading || passkeyLoading}
              onClick={() => {
                setMagicStep("form");
                setError(null);
              }}
            >
              <Wand2 />
              Email me a sign-in link
            </Button>
            {signUpSlot ? (
              <p className="text-center text-sm text-muted-foreground">{signUpSlot}</p>
            ) : null}
          </form>
        </div>
      </CardContent>
    </Card>
  );
};
